import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { academicService, discussionService } from '../services/api';
import { Spinner } from './ui';

/** Per-lesson question/reply counts for a course, with a per-lesson
 * drill-down into the actual threads. Shared shape with the teacher-facing
 * course stats modal, but focused on discussion activity rather than
 * quiz/progress numbers. */
export default function CourseCommentsModal({ open, onClose, moduleId, moduleName }) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedLessonId, setExpandedLessonId] = useState(null);
  const [threadsByLesson, setThreadsByLesson] = useState({});
  const [loadingLessonId, setLoadingLessonId] = useState(null);

  useEffect(() => {
    if (!open || !moduleId) return;
    let cancelled = false;
    setLoading(true);
    setSummary(null);
    setExpandedLessonId(null);
    setThreadsByLesson({});
    academicService.getModuleDiscussionSummary(moduleId)
      .then((res) => { if (!cancelled) setSummary(res.data || res); })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, moduleId]);

  const toggleLesson = async (lessonId) => {
    if (expandedLessonId === lessonId) {
      setExpandedLessonId(null);
      return;
    }
    setExpandedLessonId(lessonId);
    if (threadsByLesson[lessonId]) return;
    setLoadingLessonId(lessonId);
    try {
      const res = await discussionService.getThreads(lessonId);
      const data = res.data;
      setThreadsByLesson((prev) => ({ ...prev, [lessonId]: data.results || data || [] }));
    } catch (error) {
      setThreadsByLesson((prev) => ({ ...prev, [lessonId]: [] }));
    } finally {
      setLoadingLessonId(null);
    }
  };

  if (!open) return null;

  const lessons = (summary?.lessons || []).slice().sort((a, b) => b.threads_count - a.threads_count);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
        <div className="p-5 border-b border-surface-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </span>
            <div>
              <h2 className="text-lg font-display font-bold text-gray-800">{t('Course Comments')}</h2>
              <p className="text-gray-500 text-sm">{moduleName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : summary ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">{t('Questions')}</p>
                  <p className="text-2xl font-bold text-gray-800">{summary.total_threads}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">{t('Replies')}</p>
                  <p className="text-2xl font-bold text-gray-800">{summary.total_replies}</p>
                </div>
              </div>

              {lessons.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">{t('No lessons in this course yet')}</p>
              ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
                  {lessons.map((lesson) => (
                    <div key={lesson.lesson_id}>
                      <button
                        type="button"
                        onClick={() => toggleLesson(lesson.lesson_id)}
                        disabled={lesson.threads_count === 0}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition disabled:cursor-default disabled:hover:bg-white"
                      >
                        <div>
                          <p className="font-medium text-gray-800">{lesson.lesson_title}</p>
                          <p className="text-xs text-gray-400">{lesson.category_name}</p>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600 shrink-0">
                          <span>{t('{{count}} questions', { count: lesson.threads_count })}</span>
                          <span className="text-gray-300">·</span>
                          <span>{t('{{count}} replies', { count: lesson.replies_count })}</span>
                          {lesson.threads_count > 0 && (
                            <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedLessonId === lesson.lesson_id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                      </button>
                      {expandedLessonId === lesson.lesson_id && (
                        <div className="bg-gray-50 px-4 py-3 space-y-2">
                          {loadingLessonId === lesson.lesson_id ? (
                            <div className="flex justify-center py-4"><Spinner size={4} /></div>
                          ) : (threadsByLesson[lesson.lesson_id] || []).map((thread) => (
                            <div key={thread.id} className="bg-white border border-gray-100 rounded-lg p-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-medium text-gray-800 text-sm">{thread.title}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {thread.author?.first_name} {thread.author?.last_name} · {new Date(thread.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {thread.is_resolved && (
                                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{t('Resolved')}</span>
                                  )}
                                  <span className="text-xs text-gray-400">{t('{{count}} replies', { count: thread.replies_count })}</span>
                                </div>
                              </div>
                              <Link
                                to={`/modules/${moduleId}/lessons/${lesson.lesson_id}`}
                                className="mt-2 inline-block text-xs font-medium text-primary-600 hover:underline"
                              >
                                {t('Open lesson')} →
                              </Link>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">{t('Failed to load comments')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
